<template>
	<div v-if="visible" class="fd-grid" :class="clazz" :style="gridStyle" ref="gridElement">
		<component v-for="pt of gridParts" :is="componentOfPart(pt)" :part="pt" :data="localData" :readonly="ro" :viewmode="vm"
			:style="pt.extraStyle"></component>
	</div>
</template>

<script setup lang="ts">
import useContainer, {type ContainerProps, containerProps} from "./Container";
import {computed, type CSSProperties, onMounted, onUnmounted, ref, toRaw} from "vue";
import type {Grid, GridSize} from "@t/fd/layout/Grid";
import type {FormPart} from "@t/fd/FormPart";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference";
import {observeWidth} from "@/utils/Container.ts";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;

const gr = computed<Grid>(() => props.part as Grid);
const {visible, clazz, style, componentOfPart, partRef, localData, ro, vm} = useContainer(props);
const gridElement = ref<HTMLDivElement|null>(null);
const windowWidth = observeWidth(gridElement);

/* Picks correct sizing */
const sizing = computed<GridSize>(() => {
    for (let sz of gr.value.sizes) {
        if (typeof sz.maxWidth !== "number" || sz.maxWidth >= windowWidth.value) return sz;
    }
    if (gr.value.sizes.length > 0) return gr.value.sizes[gr.value.sizes.length - 1];
    return {areas: []};  // fallback if empty array
});

const requiredAreas = computed<Set<string>>(() => {
    const s = new Set<string>();
    for (let row of sizing.value.areas) {
        let segments = row.split(/(\s+)/);
        for (let segment of segments) {
            const sg = segment.trim();
            if (sg !== "." && sg.length > 0) s.add(sg);
        }
    }
    return s;
});

export interface FormPartWithExtraStyle extends FormPart {
    extraStyle: CSSProperties
}

const gridParts = computed<FormPartWithExtraStyle[]>(() => {
    const parts: FormPartWithExtraStyle[] = [];
    const ars = requiredAreas.value;
    for (let a of ars) {
        let pt: FormPartOrReference = gr.value.parts[a];
        if (!pt) pt = a;  // use area name as a reference of form part
        if (typeof pt === "string") pt = partRef(pt);

        // Add special CSS style to the part
        const ptExtended: FormPartWithExtraStyle = {
            ...toRaw(pt),
            extraStyle: {gridArea: a}
        };

        // if (typeof ptExtended.options === "undefined") ptExtended.options = {};
        // const areaStyle = "grid-area:" + a;
        // if (typeof ptExtended.options.style === "undefined" || ptExtended.options.style.trim().length === 0) ptExtended.options.style = areaStyle;
        // else ptExtended.options.style = areaStyle+";"+ptExtended.options.style;
        // console.log("   ops " + JSON.stringify(ptExtended.options));

        parts.push(ptExtended);
    }
    return parts;
});

const gridStyle = computed<CSSProperties>(() => {
    let s: CSSProperties = {
        ...style.value
    };
    let areas = "";
    for (let row of sizing.value.areas) {
        areas += ' "' + row + '"';
    }
    s.gridTemplateAreas = areas.trim();
    return s;
});

</script>

<style>
.fd-grid {
	display: grid;
}
</style>
